import * as THREE from 'three';

export const poseJointDefinitions = [
  { id:'head', label:'头部', boneName:'Bip001_Head', group:'torso' },
  { id:'neck', label:'颈部', boneName:'Bip001_Neck', group:'torso' },
  { id:'chest', label:'胸部', boneName:'Bip001_Spine2', group:'torso' },
  { id:'waist', label:'腰部', boneName:'Bip001_Spine1', group:'torso' },
  { id:'pelvis', label:'骨盆', boneName:'Bip001_Pelvis', group:'torso' },
  { id:'leftClavicle', label:'左锁骨', boneName:'Bip001_L_Clavicle', group:'arms' },
  { id:'leftUpperArm', label:'左上臂', boneName:'Bip001_L_UpperArm', group:'arms' },
  { id:'leftForearm', label:'左前臂', boneName:'Bip001_L_Forearm', group:'arms' },
  { id:'leftHand', label:'左手腕', boneName:'Bip001_L_Hand', group:'arms' },
  { id:'rightClavicle', label:'右锁骨', boneName:'Bip001_R_Clavicle', group:'arms' },
  { id:'rightUpperArm', label:'右上臂', boneName:'Bip001_R_UpperArm', group:'arms' },
  { id:'rightForearm', label:'右前臂', boneName:'Bip001_R_Forearm', group:'arms' },
  { id:'rightHand', label:'右手腕', boneName:'Bip001_R_Hand', group:'arms' },
  { id:'leftThigh', label:'左大腿', boneName:'Bip001_L_Thigh', group:'legs' },
  { id:'leftCalf', label:'左小腿', boneName:'Bip001_L_Calf', group:'legs' },
  { id:'leftFoot', label:'左脚踝', boneName:'Bip001_L_Foot', group:'legs' },
  { id:'rightThigh', label:'右大腿', boneName:'Bip001_R_Thigh', group:'legs' },
  { id:'rightCalf', label:'右小腿', boneName:'Bip001_R_Calf', group:'legs' },
  { id:'rightFoot', label:'右脚踝', boneName:'Bip001_R_Foot', group:'legs' },
  { id:'hairBackCenter', label:'后发中央', boneName:'+HairB_CB_A01', group:'hair' },
  { id:'hairBackLeft', label:'后发左', boneName:'+HairB_L_A02', group:'hair' },
  { id:'hairBackRight', label:'后发右', boneName:'+HairB_R_A02', group:'hair' },
  { id:'hairBackStrandLeft', label:'后发束左', boneName:'+HairB_L_B01', group:'hair' },
  { id:'hairBackStrandRight', label:'后发束右', boneName:'+HairB_R_B01', group:'hair' },
  { id:'hairSideLeft', label:'侧发左', boneName:'+HairS_L_A01', group:'hair' },
  { id:'hairSideRight', label:'侧发右', boneName:'+HairS_R_A01', group:'hair' },
  { id:'hairFrontLeft', label:'前发左', boneName:'+HairF_L_A01', group:'hair' },
  { id:'hairFrontRight', label:'前发右', boneName:'+HairF_R_A01', group:'hair' },
] as const;

export type PoseJointId = (typeof poseJointDefinitions)[number]['id'];

export type PoseJointBinding = {
  bone:THREE.Bone;
  copies:Array<{ bone:THREE.Bone; restQuaternion:THREE.Quaternion }>;
};

export type PoseRig = Map<PoseJointId, PoseJointBinding>;

export function bindPoseRig(root:THREE.Object3D):PoseRig {
  const bones = new Map<string, THREE.Bone[]>();
  root.traverse((node) => {
    if (!(node instanceof THREE.Bone)) return;
    const matches = bones.get(node.name) ?? [];
    matches.push(node);
    bones.set(node.name, matches);
  });
  const rig:PoseRig = new Map();
  poseJointDefinitions.forEach((definition) => {
    const matches = bones.get(definition.boneName);
    if (matches?.length) {
      const drivers = matches.filter((bone) => {
        let parent = bone.parent;
        while (parent) {
          if (parent instanceof THREE.Bone && parent.name === bone.name) return false;
          parent = parent.parent;
        }
        return true;
      });
      const controlledBones = drivers.length ? drivers : [matches[0]];
      rig.set(definition.id, {
        bone:controlledBones[0],
        copies:controlledBones.map((bone) => ({ bone, restQuaternion:bone.quaternion.clone() })),
      });
    }
  });
  return rig;
}

export function resetPoseJoint(binding:PoseJointBinding) {
  binding.copies.forEach(({ bone, restQuaternion }) => {
    bone.quaternion.copy(restQuaternion);
    bone.updateMatrixWorld(true);
  });
}

export function resetPoseRig(rig:PoseRig) {
  rig.forEach(resetPoseJoint);
}

export function getPoseOffsetEuler(binding:PoseJointBinding) {
  return new THREE.Euler().setFromQuaternion(getPoseOffsetQuaternion(binding), 'XYZ');
}

export function getPoseOffsetQuaternion(binding:PoseJointBinding) {
  const primary = binding.copies[0];
  return primary.restQuaternion.clone().invert().multiply(primary.bone.quaternion).normalize();
}

export function setPoseOffsetEuler(binding:PoseJointBinding, euler:THREE.Euler) {
  setPoseOffsetQuaternion(binding, new THREE.Quaternion().setFromEuler(euler));
}

export function setPoseOffsetQuaternion(binding:PoseJointBinding, offset:THREE.Quaternion) {
  const primary = binding.copies[0];
  primary.bone.quaternion.copy(primary.restQuaternion).multiply(offset.clone().normalize());
  primary.bone.updateMatrixWorld(true);
  syncPoseJointCopies(binding);
}

export function syncPoseJointCopies(binding:PoseJointBinding) {
  const primary = binding.copies[0];
  primary.bone.updateWorldMatrix(true, false);
  const primaryParentWorld = primary.bone.parent
    ? primary.bone.parent.getWorldQuaternion(new THREE.Quaternion())
    : new THREE.Quaternion();
  const primaryBaseWorld = primaryParentWorld.clone().multiply(primary.restQuaternion);
  const primaryCurrentWorld = primary.bone.getWorldQuaternion(new THREE.Quaternion());
  const worldDelta = primaryCurrentWorld.multiply(primaryBaseWorld.invert());
  binding.copies.slice(1).forEach(({ bone, restQuaternion }) => {
    bone.parent?.updateWorldMatrix(true, false);
    const parentWorld = bone.parent
      ? bone.parent.getWorldQuaternion(new THREE.Quaternion())
      : new THREE.Quaternion();
    const targetBaseWorld = parentWorld.clone().multiply(restQuaternion);
    const targetWorld = worldDelta.clone().multiply(targetBaseWorld);
    bone.quaternion.copy(parentWorld.invert().multiply(targetWorld));
    bone.updateMatrixWorld(true);
  });
}
